/** @jsxImportSource @emotion/react */
import { useRef, useState } from 'react';

import { css, useTheme } from '@emotion/react';
import Slider from 'react-slick';
import slide1 from 'static/main/section2_1.png';
import slide2 from 'static/main/section2_2.png';
import slide3 from 'static/main/section2_3.png';
import slideM1 from 'static/main/section2_m1.png';
import slideM2 from 'static/main/section2_m2.png';
import slideM3 from 'static/main/section2_m3.png';

const Section2 = () => {
  const [active, setActive] = useState(0);
  const { mq } = useTheme();
  const slideSliderRef = useRef<Slider | null>(null);
  const [throttle, setThrottle] = useState(false);

  const section2Style = css`
    padding: 220px 0 140px 0;
    background-color: #fff;
    .container {
      max-width: 1535px;
      padding: 0 40px;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      h2 {
        font-size: 56px;
        font-weight: 600;
        line-height: 76px;
        color: #6079ff;
        span {
          color: #58b139;
          font-size: inherit;
          font-weight: inherit;
        }
      }
      .list {
        width: 368px;
        margin-top: 56px;
        li {
          display: flex;
          padding: 24px 0px;
          cursor: pointer;
          .step {
            color: #6079ff;
            font-size: 14px;
            font-weight: bold;
            margin-right: 6px;
          }
          .title {
            font-weight: 600;
          }
          .content {
            display: none;
            white-space: pre-wrap;
          }
          .img {
            display: none;
          }
          &.active {
            transition: all 0.3s;
            flex-direction: column;
            .title {
              font-size: 28px;
              font-weight: 600;
              line-height: 42px;
            }
            .content {
              display: block;
              white-space: pre-wrap;
              line-height: 28px;
              font-size: 18px;
            }
          }
        }
        li + li {
          border-top: 1px solid #e4e7ec;
        }
      }
      .slide {
        width: 742px;
        img {
          width: 100%;
        }
        .slick-track {
          display: flex;
          align-items: center;
          height: 100%;
        }
      }
    }

    ${mq.mobile} {
      padding: 84px 0px 42px 0px;
      .container {
        padding: 0 32px;
        .slide {
          display: none;
        }
        h2 {
          font-size: 28px;
          line-height: 36px;
        }
        .list {
          width: auto;
          margin-top: 14px;
          li {
            flex-direction: column;
            padding: 42px 0;
            .title {
              font-size: 20px;
              font-weight: 600;
              line-height: 30px;
              margin-top: 2px;
            }
            .img {
              display: block;
              margin-top: 24px;
              img {
                width: 100%;
              }
            }
            .content {
              margin-top: 6px;
              display: block;
              white-space: pre-wrap;
              line-height: 22px;
              font-size: 14px;
              margin-top: 0px;
            }
            &:nth-of-type(1),
            &:nth-of-type(3) {
              .img {
                margin-right: -32px;
              }
            }
            &.active {
              .title {
                font-size: 20px;
                font-weight: 600;
                line-height: 30px;
                margin-top: 2px;
              }
              .content {
                white-space: pre-wrap;
                line-height: 22px;
                font-size: 14px;
                margin-top: 0px;
              }
            }
          }
        }
      }
    }
  `;

  const contents = [
    {
      title: '상품 선택',
      text: '순식간에 나의 쇼핑몰이 만들어집니다.\n관심분야를 선택하면 내 쇼핑몰에 적합한 상품을\n추천해드립니다.',
      img: slide1,
      mImg: slideM1,
    },
    {
      title: '몰 꾸미기',
      text: '내가 원하는 느낌과 색상으로 몰을 꾸며보세요\n직접 만든 이미지나, 직접 찍은 사진이면 좋아요.\n좋아하는 색상도 꼭! 지정해보세요.',
      img: slide2,
      mImg: slideM2,
    },
    {
      title: '완성 및 링크 공유',
      text: '만들어진 쇼핑몰의 링크를 친구, 지인, 가족에게 알려주세요.\n구매가 일어나면 수익이 차곡 차곡 쌓입니다',
      img: slide3,
      mImg: slideM3,
    },
  ];

  const onClickList = (index: number) => {
    if (throttle) return;
    if (!throttle) {
      setThrottle(true);
      slideSliderRef.current?.slickGoTo(index);
      setActive(index);
      setTimeout(async () => {
        setThrottle(false);
      }, 500);
    }
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    swipe: false,
    arrows: false,
  };

  return (
    <div css={section2Style}>
      <div className="container">
        <div className="left">
          <h2>
            쉽고 빠르게
            <br />
            <span>쇼핑몰을 만드는 방법</span>
          </h2>
          <ul className="list">
            {contents.map((v, k) => (
              <li
                key={k}
                className={k === active ? 'active' : ''}
                onClick={() => onClickList(k)}
              >
                <div className="step">STEP {k + 1}.</div>
                <div className="title">{v.title}</div>
                <div className="content">{v.text}</div>
                <div className="img">
                  <img src={v.mImg} alt="슬라이드" />
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="right">
          <Slider {...settings} className="slide" ref={slideSliderRef}>
            {contents.map((v, k) => (
              <img src={v.img} alt="슬라이드 이미지" key={k} />
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default Section2;
